import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Search, StickyNote, Trash2 } from "lucide-react";
import { Editor } from "@tinymce/tinymce-react";
import "tinymce/tinymce";
import "tinymce/models/dom";
import "tinymce/themes/silver";
import "tinymce/icons/default";
import "tinymce/plugins/image";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/skins/ui/oxide/skin.min.css";
import "./notes-page.css";
import { toast } from "sonner";
import { createNote, deleteNote, listNotes, updateNote } from "../../api/notes";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import type { Note } from "../../lib/types";
import { cn, formatDate } from "../../lib/utils";

type SaveState = "idle" | "dirty" | "saving" | "saved" | "error";

function noteTitle(note: Note | null | undefined) {
  return note?.title.trim() || "未命名便签";
}

function summaryText(note: Note) {
  return note.content_text.trim() || "暂无内容";
}

function saveLabel(state: SaveState, error: string) {
  if (state === "saving") return "保存中";
  if (state === "saved") return "已保存";
  if (state === "error") return error || "保存失败";
  if (state === "dirty") return "待保存";
  return "";
}

export function NotesPage() {
  const queryClient = useQueryClient();
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const loadedIdRef = useRef("");
  const savedSnapshotRef = useRef("");
  const [keyword, setKeyword] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftHtml, setDraftHtml] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState("");

  const notesQuery = useQuery({
    queryKey: ["notes", keyword],
    queryFn: () => listNotes({ keyword })
  });
  const notes = notesQuery.data?.notes || [];
  const selectedNote = useMemo(() => notes.find((note) => note.id === selectedId) || null, [notes, selectedId]);
  const selectedIndex = useMemo(() => notes.findIndex((note) => note.id === selectedId), [notes, selectedId]);

  useEffect(() => {
    if (!notes.length) {
      setSelectedId("");
      return;
    }
    if (!selectedId || !notes.some((note) => note.id === selectedId)) {
      setSelectedId(notes[0].id);
    }
  }, [notes, selectedId]);

  useEffect(() => {
    if (!selectedNote) {
      loadedIdRef.current = "";
      savedSnapshotRef.current = "";
      setDraftTitle("");
      setDraftHtml("");
      setSaveState("idle");
      setSaveError("");
      return;
    }
    if (loadedIdRef.current === selectedNote.id) return;
    loadedIdRef.current = selectedNote.id;
    const snapshot = JSON.stringify({ title: selectedNote.title, content_html: selectedNote.content_html });
    savedSnapshotRef.current = snapshot;
    setDraftTitle(selectedNote.title);
    setDraftHtml(selectedNote.content_html);
    setSaveState("saved");
    setSaveError("");
    setTimeout(() => titleInputRef.current?.focus(), 0);
  }, [selectedNote]);

  const createMutation = useMutation({
    mutationFn: createNote,
    onSuccess: (note) => {
      setKeyword("");
      queryClient.setQueryData<{ notes: Note[] }>(["notes", ""], (current) => ({ notes: [note, ...(current?.notes || [])] }));
      void queryClient.invalidateQueries({ queryKey: ["notes"] });
      loadedIdRef.current = "";
      setSelectedId(note.id);
      setTimeout(() => titleInputRef.current?.focus(), 0);
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "便签创建失败")
  });

  const saveMutation = useMutation({
    mutationFn: (payload: { id: string; title: string; content_html: string }) => updateNote(payload.id, { title: payload.title, content_html: payload.content_html }),
    onSuccess: (note) => {
      savedSnapshotRef.current = JSON.stringify({ title: note.title, content_html: note.content_html });
      setSaveState("saved");
      setSaveError("");
      queryClient.setQueryData<{ notes: Note[] }>(["notes", keyword], (current) => {
        const currentNotes = current?.notes || [];
        return { notes: currentNotes.map((item) => (item.id === note.id ? note : item)) };
      });
    },
    onError: (error) => {
      setSaveState("error");
      setSaveError(error instanceof Error ? error.message : "保存失败");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNote,
    onSuccess: (_, deletedId) => {
      const fallback = notes[selectedIndex + 1] || notes[selectedIndex - 1] || null;
      setSelectedId(fallback && fallback.id !== deletedId ? fallback.id : "");
      loadedIdRef.current = "";
      void queryClient.invalidateQueries({ queryKey: ["notes"] });
      toast.success("便签已删除");
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "删除失败")
  });

  useEffect(() => {
    if (!selectedId || loadedIdRef.current !== selectedId) return;
    const snapshot = JSON.stringify({ title: draftTitle, content_html: draftHtml });
    if (snapshot === savedSnapshotRef.current) return;
    setSaveState("dirty");
    setSaveError("");
    const timer = window.setTimeout(() => {
      setSaveState("saving");
      saveMutation.mutate({ id: selectedId, title: draftTitle, content_html: draftHtml });
    }, 800);
    return () => window.clearTimeout(timer);
  }, [draftHtml, draftTitle, saveMutation.mutate, selectedId]);

  function removeCurrentNote() {
    if (!selectedId || deleteMutation.isPending) return;
    if (!window.confirm(`确定删除「${noteTitle(selectedNote)}」吗？`)) return;
    deleteMutation.mutate(selectedId);
  }

  const statusText = saveLabel(saveState, saveError);

  return (
    <div className="grid h-full min-h-0 grid-cols-[320px_1fr] overflow-hidden bg-slate-100 max-md:grid-cols-1">
      <aside className="flex min-h-0 flex-col border-r border-slate-200 bg-white">
        <div className="flex h-12 items-center gap-2 border-b border-slate-200 px-3">
          <Button className="bg-slate-900 text-white hover:bg-slate-800" disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
            <Plus size={14} />
            新增
          </Button>
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-slate-400" />
            <Input className="w-full pl-7" placeholder="全文搜索" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </div>
          <Button
            aria-label="刷新便签"
            disabled={notesQuery.isFetching}
            onClick={() => {
              void notesQuery.refetch();
            }}
          >
            <RefreshCw size={14} />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-2">
          {notes.map((note) => (
            <div
              key={note.id}
              className={cn(
                "group relative mb-2 rounded-md border border-slate-200 bg-white shadow-sm transition hover:bg-slate-50",
                selectedId === note.id && "border-slate-400 bg-slate-100 shadow"
              )}
            >
              <button className="block w-full p-3 pr-9 text-left" onClick={() => setSelectedId(note.id)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 truncate text-sm font-semibold text-slate-900">{noteTitle(note)}</div>
                  <div className="shrink-0 text-[11px] text-slate-500">{formatDate(note.updated_at)}</div>
                </div>
                <div className="mt-1 line-clamp-2 text-xs leading-5 text-slate-600">{summaryText(note)}</div>
              </button>
              {selectedId === note.id ? (
                <button
                  aria-label="删除当前便签"
                  title="删除当前便签"
                  className="absolute bottom-2 right-2 grid size-6 place-items-center rounded border border-transparent text-slate-400 opacity-70 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 group-hover:opacity-100"
                  disabled={deleteMutation.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeCurrentNote();
                  }}
                >
                  <Trash2 size={13} />
                </button>
              ) : null}
            </div>
          ))}
          {!notes.length ? (
            <div className="grid h-full min-h-64 place-items-center rounded-md border border-dashed border-slate-200 text-center text-xs text-slate-500">
              {notesQuery.isLoading ? "便签加载中..." : keyword.trim() ? "没有匹配的便签" : "暂无便签"}
            </div>
          ) : null}
        </div>
      </aside>
      <section className="min-h-0 overflow-hidden p-4">
        {selectedNote ? (
          <div className="flex h-full min-h-0 flex-col gap-3">
            <div className="flex h-10 items-center gap-2">
              <Input
                ref={titleInputRef}
                className="h-9 flex-1 border-slate-300 bg-white text-sm font-semibold shadow-sm focus:border-slate-600"
                placeholder="便签标题"
                value={draftTitle}
                onChange={(event) => setDraftTitle(event.target.value)}
              />
              <div className={cn("min-w-16 text-right text-xs text-slate-500", saveState === "error" && "text-rose-600")}>{statusText}</div>
            </div>
            <div className="notes-editor-shell min-h-0 flex-1">
              <Editor
                value={draftHtml}
                licenseKey="gpl"
                onEditorChange={(value) => setDraftHtml(value)}
                init={{
                  height: "100%",
                  min_height: 420,
                  menubar: false,
                  statusbar: false,
                  branding: false,
                  resize: false,
                  plugins: "lists link image",
                  toolbar: "bold italic underline strikethrough | bullist numlist | link image",
                  skin: false,
                  content_css: false,
                  content_style:
                    "body { background: #ffffff; color: #0f172a; font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 14px; line-height: 1.45; padding: 16px 18px; } p { margin: 0 0 6px; } ul, ol { margin-top: 4px; margin-bottom: 6px; padding-left: 22px; } li { margin: 0 0 3px; }"
                }}
              />
            </div>
          </div>
        ) : (
          <div className="grid h-full place-items-center rounded-md border border-dashed border-slate-300 bg-white text-center">
            <div>
              <StickyNote size={32} className="mx-auto text-slate-400" />
              <div className="mt-3 text-sm font-semibold text-slate-700">{keyword.trim() ? "没有匹配的便签" : "暂无便签"}</div>
              <Button className="mt-4 bg-slate-900 text-white hover:bg-slate-800" disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
                <Plus size={14} />
                新增便签
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
